import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["menu", "button"];

  connect() {
    this.open = false;
    this.menuElement = this.hasMenuTarget ? this.menuTarget : null;
    this.buttonElement = this.hasButtonTarget ? this.buttonTarget : null;
    this.boundReposition = this.reposition.bind(this);
    this.boundHideOutside = this.hideOutside.bind(this);
    this.beforeCacheHandler = () => this.hide();
    document.addEventListener("turbo:before-cache", this.beforeCacheHandler);

    this.restoreMenuToController();
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCacheHandler);
    this.hide();
  }

  toggle(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.open) {
      this.hide();
    } else {
      this.closeOtherMenus();
      this.show();
    }
  }

  hide(event) {
    if (event) {
      event.preventDefault();
    }

    if (!this.open) {
      return;
    }

    this.open = false;
    const menu = this.getMenuElement();
    if (!menu) {
      return;
    }

    menu.classList.add("hidden");
    this.resetMenuPosition(menu);
    this.removeListeners();
    this.restoreMenuToController();
  }

  hideOutside(event) {
    if (!this.open) {
      return;
    }

    const menu = this.getMenuElement();
    if (this.element.contains(event.target) || (menu && menu.contains(event.target))) {
      return;
    }

    this.hide();
  }

  show() {
    const menu = this.getMenuElement();
    const button = this.getButtonElement();
    if (!menu || !button) {
      return;
    }

    this.open = true;
    document.body.appendChild(menu);
    menu.classList.remove("hidden");
    this.reposition();
    this.addListeners();
  }

  onMenuClick(event) {
    const menu = this.getMenuElement();
    if (!menu || !menu.contains(event.target)) {
      return;
    }

    const transferButton = event.target.closest("[data-qc-reassign-open]");
    if (transferButton) {
      event.preventDefault();
      event.stopPropagation();
      this.openQcReassign(transferButton);
      this.hide();
      return;
    }

    const actionable = event.target.closest("a, button");
    if (actionable) {
      setTimeout(() => this.hide(), 0);
    }
  }

  openQcReassign(button) {
    const root = document.querySelector('[data-controller~="qc-reassign"]');
    if (!root) return;

    const controller = this.application.getControllerForElementAndIdentifier(
      root,
      "qc-reassign"
    );
    controller?.openFromButton(button);
  }

  reposition() {
    if (!this.open) {
      return;
    }

    const button = this.getButtonElement();
    const menu = this.getMenuElement();
    if (!button || !menu) {
      return;
    }

    const gap = 4;
    const viewportPadding = 8;
    const buttonRect = button.getBoundingClientRect();

    menu.style.position = "fixed";
    menu.style.zIndex = "9999";
    menu.style.right = "auto";
    menu.style.bottom = "auto";
    menu.style.visibility = "hidden";
    menu.style.top = "0";
    menu.style.left = "0";
    menu.style.maxHeight = `${window.innerHeight - viewportPadding * 2}px`;
    menu.style.overflowY = "auto";

    const menuRect = menu.getBoundingClientRect();
    menu.style.visibility = "";

    let top = buttonRect.bottom + gap;
    let openAbove = false;

    const spaceBelow = window.innerHeight - viewportPadding - buttonRect.bottom - gap;
    const spaceAbove = buttonRect.top - viewportPadding - gap;

    if (menuRect.height > spaceBelow && spaceAbove > spaceBelow) {
      top = buttonRect.top - gap - menuRect.height;
      openAbove = true;
    }

    if (top + menuRect.height > window.innerHeight - viewportPadding) {
      top = Math.max(viewportPadding, window.innerHeight - viewportPadding - menuRect.height);
    }

    if (top < viewportPadding) {
      top = viewportPadding;
    }

    let left = buttonRect.right - menuRect.width;
    if (left < viewportPadding) {
      left = viewportPadding;
    }
    if (left + menuRect.width > window.innerWidth - viewportPadding) {
      left = Math.max(viewportPadding, window.innerWidth - viewportPadding - menuRect.width);
    }

    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;
    menu.classList.toggle("stack-actions-dropdown-menu--above", openAbove);
  }

  resetMenuPosition(menu) {
    menu.style.position = "";
    menu.style.top = "";
    menu.style.left = "";
    menu.style.right = "";
    menu.style.bottom = "";
    menu.style.zIndex = "";
    menu.style.maxHeight = "";
    menu.style.overflowY = "";
    menu.style.visibility = "";
    menu.classList.remove("stack-actions-dropdown-menu--above");
  }

  getMenuElement() {
    if (this.menuElement && document.body.contains(this.menuElement)) {
      return this.menuElement;
    }

    this.menuElement = this.hasMenuTarget
      ? this.menuTarget
      : this.element.querySelector('[data-stack-actions-dropdown-target="menu"]');

    return this.menuElement;
  }

  getButtonElement() {
    if (this.buttonElement && document.body.contains(this.buttonElement)) {
      return this.buttonElement;
    }

    this.buttonElement = this.hasButtonTarget
      ? this.buttonTarget
      : this.element.querySelector('[data-stack-actions-dropdown-target="button"]');

    return this.buttonElement;
  }

  restoreMenuToController() {
    const menu = this.getMenuElement();
    if (menu && menu.parentElement !== this.element) {
      this.element.appendChild(menu);
    }
  }

  addListeners() {
    this.boundMenuClick = this.boundMenuClick || this.onMenuClick.bind(this);
    window.addEventListener("resize", this.boundReposition);
    window.addEventListener("scroll", this.boundReposition, true);
    document.addEventListener("click", this.boundHideOutside);

    const menu = this.getMenuElement();
    if (menu) {
      menu.addEventListener("click", this.boundMenuClick);
    }
  }

  removeListeners() {
    window.removeEventListener("resize", this.boundReposition);
    window.removeEventListener("scroll", this.boundReposition, true);
    document.removeEventListener("click", this.boundHideOutside);

    const menu = this.getMenuElement();
    if (menu && this.boundMenuClick) {
      menu.removeEventListener("click", this.boundMenuClick);
    }
  }

  closeOtherMenus() {
    document.querySelectorAll('[data-controller~="stack-actions-dropdown"]').forEach((element) => {
      if (element === this.element) {
        return;
      }

      const controller = this.application.getControllerForElementAndIdentifier(
        element,
        "stack-actions-dropdown"
      );

      if (controller?.open) {
        controller.hide();
      }
    });
  }
}
